<template>
  <div :class="wrapClasses">
      <div :class="[prefixCls + '-group-prepend']" v-if="prepend" v-show="slotReady"><slot name="prepend"></slot></div>
      <i class="am-icon" :class="['am-icon-ios-close-circle', prefixCls + '-icon', prefixCls + '-icon-clear' , prefixCls + '-icon-normal']" v-if="clearable && currentValue" @click="handleClear">
        ×
      </i>
      <i class="am-icon" :class="['am-icon-' + icon, prefixCls + '-icon', prefixCls + '-icon-normal']" v-else-if="icon" @click="handleIconClick"></i>
      <i class="am-icon am-icon-ios-search" :class="[prefixCls + '-icon', prefixCls + '-icon-normal', prefixCls + '-search-icon']" v-else-if="search && enterButton === false" @click="handleSearch"></i>
      <span class="am-input-suffix" v-else-if="showSuffix"><slot name="suffix"><i class="am-icon" :class="['am-icon-' + suffix]" v-if="suffix"></i></slot></span>
      <transition name="fade">
        <i class="am-icon am-icon-ios-loading am-load-loop" :class="[prefixCls + '-icon', prefixCls + '-icon-validate']" v-if="!icon"></i>
      </transition>
      <input
        :id="elementId"
        ref="input"
        :type="type"
        :class="inputClasses"
        :placeholder="placeholder"
        :disabled="disabled"
        :maxlength="maxlength"
        :readonly="readonly"
        :name="name"
        :value="currentValue"
        :number="number"
        :autofocus="autofocus"
        @keyup.enter="handleEnter"
        @keyup="handleKeyup"
        @keypress="handleKeypress"
        @keydown="handleKeydown"
        @focus="handleFocus"
        @blur="handleBlur"
        @input="handleInput"
        @change="handleChange">
      <div :class="[prefixCls + '-group-append']" v-if="append" v-show="slotReady"><slot name="append"></slot></div>
      <div :class="[prefixCls + '-group-append', prefixCls + '-search']" v-else-if="search && enterButton" @click="handleSearch">
        <i class="am-icon am-icon-ios-search" v-if="enterButton === true"></i>
        <template v-else>{{ enterButton }}</template>
      </div>
      <span class="am-input-prefix" v-else-if="showPrefix"><slot name="prefix"><i class="am-icon" :class="['am-icon-' + prefix]" v-if="prefix"></i></slot></span>
  </div>
</template>

<script>
  import { oneOf } from './../assist'
  const prefixCls = 'am-input';
    export default {
        name: 'Input',
        props: {
          type: {
            validator (value) {
              return oneOf(value, ['text', 'textarea', 'password', 'url', 'email', 'date', 'number']);
            },
            default: 'text'
          },
          value: {
            type: [String, Number],
            default: ''
          },
          size: {
            validator (value) {
              return oneOf(value, ['small', 'large', 'middle', 'default']);
            },
            default () {
              return 'default'
            }
          },
          placeholder: {
            type: String,
            default: ''
          },
          maxlength: {
            type: Number
          },
          disabled: {
            type: Boolean,
            default: false
          },
          icon: String,
          autosize: {
            type: [Boolean, Object],
            default: false
          },
          readonly: {
            type: Boolean,
            default: false
          },
          name: {
            type: String
          },
          number: {
            type: Boolean,
            default: false
          },
          autofocus: {
            type: Boolean,
            default: false
          },
          clearable: {
            type: Boolean,
            default: false
          },
          elementId: {
            type: String
          },
          prefix: {
            type: String,
            default: ''
          },
          suffix: {
            type: String,
            default: ''
          },
          search: {
            type: Boolean,
            default: false
          },
          enterButton: {
            type: [Boolean, String],
            default: false
          }
        },
        created() {
        },
        data() {
            return {
              currentValue: this.value,
              prefixCls: prefixCls,
              prepend: true,
              append: true,
              slotReady: false,
              showPrefix: false,
              showSuffix: false
            }
        },
        mounted() {
          if (this.type !== 'textarea') {
            this.prepend = this.$slots.prepend !== undefined;
            this.append = this.$slots.append !== undefined;
            this.showPrefix = this.prefix !== '' || this.$slots.prefix !== undefined;
            this.showSuffix = this.suffix !== '' || this.$slots.suffix !== undefined;
          } else {
            this.prepend = false;
            this.append = false;
          }
          this.slotReady = true;
        },
        computed: {
          wrapClasses () {
            return [
              `${prefixCls}-wrapper`,
              {
                [`${prefixCls}-wrapper-${this.size}`]: !!this.size,
                [`${prefixCls}-type`]: this.type,
                [`${prefixCls}-group`]: this.prepend || this.append || (this.search && this.enterButton),
                [`${prefixCls}-group-${this.size}`]: (this.prepend || this.append || (this.search && this.enterButton)) && !!this.size,
                [`${prefixCls}-group-with-prepend`]: this.prepend,
                [`${prefixCls}-group-with-append`]: this.append || (this.search && this.enterButton),
                [`${prefixCls}-hide-icon`]: this.append,  // #554
                [`${prefixCls}-with-search`]: (this.search && this.enterButton)
              }
            ];
          },
          inputClasses () {
            return [
              `${prefixCls}`,
              {
                [`${prefixCls}-${this.size}`]: !!this.size,
                [`${prefixCls}-disabled`]: this.disabled,
                [`${prefixCls}-with-prefix`]: this.showPrefix,
                [`${prefixCls}-with-suffix`]: this.showSuffix || (this.search && this.enterButton === false)
              }
            ];
          },
        },
        methods: {
          handleEnter (event) {
            this.$emit('on-enter', event);
            if (this.search) this.$emit('on-search', this.currentValue);
          },
          handleKeydown (event) {
            this.$emit('on-keydown', event);
          },
          handleKeypress(event) {
            this.$emit('on-keypress', event);
          },
          handleKeyup (event) {
            this.$emit('on-keyup', event);
          },
          handleIconClick (event) {
            this.$emit('on-click', event);
          },
          handleFocus (event) {
            this.$emit('on-focus', event);
          },
          handleBlur (event) {
            this.$emit('on-blur', event);
          },
          handleInput (event) {
            let value = event.target.value;
            if (this.number && value !== '') value = Number.isNaN(Number(value)) ? value : Number(value);
            this.$emit('input', value);
            this.setCurrentValue(value);
            this.$emit('on-change', event);
          },
          handleChange (event) {
            this.$emit('on-input-change', event);
          },
          setCurrentValue (value) {
            if (value === this.currentValue) return;
            this.$nextTick(() => {
              this.resizeTextarea();
            });
            this.currentValue = value;
          },
          resizeTextarea () {
            const autosize = this.autosize;
            if (!autosize || this.type !== 'textarea') {
              return false;
            }

            const minRows = autosize.minRows;
            const maxRows = autosize.maxRows;

            this.textareaStyles = calcTextareaHeight(this.$refs.textarea, minRows, maxRows);
          },
          focus () {
            if (this.type === 'textarea') {
              this.$refs.textarea.focus();
            } else {
              this.$refs.input.focus();
            }
          },
          blur () {
            if (this.type === 'textarea') {
              this.$refs.textarea.blur();
            } else {
              this.$refs.input.blur();
            }
          },
          handleClear () {
            const e = { target: { value: '' } };
            this.$emit('input', '');
            this.setCurrentValue('');
            this.$emit('on-change', e);
          },
          handleSearch () {
            if (this.disabled) return false;
            this.$refs.input.focus();
            this.$emit('on-search', this.currentValue);
          }
        },
        watch: {
          value (val) {
            this.setCurrentValue(val);
          }
        },
        destroyed() {
        }
    }
</script>

<style lang="less">
  @css-prefix:'am-';
  @input-prefix-cls: ~"@{css-prefix}input";
  // Background color
  @background-color-base        : #f7f7f7;  // base
  // Border color
  @border-color-base      : #dcdee2;  // outside
  @border-color-split     : #e8eaec;  // inside
  @border-width-base      : 1px;            // width of the border for a component
  @border-style-base      : solid;          // style of a components border
  // Input
  @input-height-base           : 32px;
  @input-height-large          : 36px;
  @input-height-small          : 24px;
  @input-height-middle         : 30px;

  @input-padding-horizontal    : 7px;
  @input-padding-vertical-base : 4px;
  @input-padding-vertical-small: 1px;
  @input-padding-vertical-large: 6px;

  @input-placeholder-color     : @btn-disable-color;
  @input-color                 : @text-color;
  @input-border-color          : @border-color-base;
  @input-bg                    : #fff;
  @input-group-bg              : #f8f8f9;

  @input-hover-border-color    : @primary-color;
  @input-focus-border-color    : @primary-color;
  @input-disabled-bg           : #f3f3f3;
  // Color
  @primary-color          : #2d8cf0;
  @info-color             : #2db7f5;
  @success-color          : #19be6b;
  @processing-color       : @primary-color;
  @warning-color          : #ff9900;
  @error-color            : #ed4014;
  @normal-color           : #e6ebf1;
  @link-color             : #2D8cF0;
  @link-hover-color       : tint(@link-color, 20%);
  @link-active-color      : shade(@link-color, 5%);
  @selected-color         : fade(@primary-color, 90%);
  @tooltip-color          : #fff;
  @subsidiary-color       : #808695;
  @rate-star-color        : #f5a623;
  // Base
  @body-background        : #fff;
  @font-family            : "Helvetica Neue",Helvetica,"PingFang SC","Hiragino Sans GB","Microsoft YaHei","微软雅黑",Arial,sans-serif;
  @code-family            : Consolas,Menlo,Courier,monospace;@title-color            : #17233d;

  @text-color             : #333;
  @font-size-base         : 14px;
  @font-size-small        : 12px;
  @font-size-large        : 16px;
  @line-height-base       : 1.5;
  @line-height-computed   : floor((@font-size-base * @line-height-base));
  @border-radius-base     : 6px;
  @cursor-disabled        : not-allowed;
  // Animation
  @animation-time         : .3s;
  @transition-time        : .2s;
  @ease-in-out            : ease-in-out;
  // Button
  @btn-font-weight        : normal;
  @btn-padding-base       : 5px 15px 6px;
  @btn-padding-large      : 6px 15px 6px 15px;
  @btn-padding-small      : 1px 7px 2px;
  @btn-padding-base-icon  : 5px 15px 6px;
  @btn-padding-large-icon : 6px 15px 6px 15px;
  @btn-padding-small-icon : 1px 7px 2px;
  @btn-font-size          : 12px;
  @btn-font-size-large    : 14px;
  @btn-border-radius      : 4px;
  @btn-border-radius-small: 3px;
  @btn-group-border       : shade(@primary-color, 5%);
  @btn-disable-color      : #c5c8ce;
  @btn-disable-bg         : @background-color-base;
  @btn-disable-border     : @border-color-base;
  .@{input-prefix-cls} {
    .input;
    &-wrapper{
      display: inline-block;
      width: 100%;
      position: relative;
      vertical-align: middle;

      // #2149 & #2219
      line-height: normal
    }
    &-icon {
      width: 32px;
      height: @input-height-base;
      line-height: @input-height-base;
      font-size: 16px;
      text-align: center;
      color: @subsidiary-color;
      position: absolute;
      right: 0;
      z-index: 3;
    }
    &-hide-icon &-icon{
      display: none;
    }
    &-icon-validate{
      display: none;
    }

    &-icon-clear{
      display: none;
      font-weight: bold;
      cursor: pointer;
    }
    &-wrapper:hover{
      .@{input-prefix-cls}-icon-clear{
        display: inline-block;
      }
    }

    &-icon-normal + &{
      padding-right: 32px;
    }
    // #554
    &-hide-icon &-icon-normal + &{
      padding-right: @input-padding-horizontal;
    }

    &-wrapper-large &-icon{
      font-size: 18px;
      height: @input-height-large;
      line-height: @input-height-large;
    }
    &-wrapper-small &-icon{
      width: 24px;
      font-size: 14px;
      height: @input-height-small;
      line-height: @input-height-small;

      //+ .@{input-prefix-cls} {
      //    padding-right: 24px;
      //}
    }

    // prefix & suffix
    &-prefix, &-suffix{
      width: 32px;
      height: 100%;
      text-align: center;
      position: absolute;
      left: 0;
      top: 0;
      z-index: 1;
      i{
        font-size: 16px;
        line-height: @input-height-base;
        color: @subsidiary-color;
      }
    }
    &-suffix{
      left: auto;
      right: 0;
    }
    &-wrapper-small &-prefix, &-wrapper-small &-suffix{
      i{
        font-size: 14px;
        line-height: @input-height-small;
      }
    }
    &-wrapper-large &-prefix, &-wrapper-large &-suffix{
      i{
        font-size: 18px;
        line-height: @input-height-large;
      }
    }

    &-with-prefix{
      padding-left: 32px;
    }
    &-with-suffix{
      padding-right: 32px;
    }

    // search
    &-search{
      cursor: pointer;
      padding: 0 16px !important;
      background: @primary-color !important;
      color: #fff !important;
      border-color: @primary-color !important;
      transition: all @transition-time @ease-in-out;
      position: relative;
      z-index: 2;

      i{
        font-size: 16px;
      }

      &:hover{
        background: tint(@primary-color, 20%) !important;
        border-color: tint(@primary-color, 20%) !important;
      }
      &:active{
        background: shade(@primary-color, 5%) !important;
        border-color: shade(@primary-color, 5%) !important;
      }

      &-icon{
        cursor: pointer;
        transition: color @transition-time @ease-in-out;
        &:hover{
          color: inherit;
        }
      }

      &:before{
        content: '';
        display: block;
        width: 1px;
        position: absolute;
        top: -1px;
        bottom: -1px;
        left: -1px;
        background: inherit;
      }
    }
    &-wrapper-small &-search{
      padding: 0 12px !important;
      i{
        font-size: 14px;
      }
    }
    &-wrapper-large &-search{
      padding: 0 20px !important;
      i{
        font-size: 18px;
      }
    }
    &-with-search{
      &:hover{
        .@{input-prefix-cls} {
          border-color: tint(@primary-color, 20%);
        }
      }
    }
  }
  .@{input-prefix-cls}-group{
    .input-group(~"@{input-prefix-cls}");
  }
  .hover(@color: @input-hover-border-color) {
    border-color: tint(@color, 20%);
  }
  .hover-error() {
    border-color: @error-color;
  }

  .active(@color: @input-hover-border-color) {
    border-color: tint(@color, 20%);
    outline: 0;
    box-shadow: 0 0 0 2px fade(@color, 20%);
  }
  .active-error() {
    border-color: @error-color;
    outline: 0;
    box-shadow: 0 0 0 2px fade(@error-color, 20%);
  }

  .disabled() {
    background-color: @input-disabled-bg;
    opacity: 1;
    cursor: @cursor-disabled;
    color: #ccc;
    &:hover {
      .hover(@input-border-color);
    }
  }

  .input-large() {
    font-size: @font-size-base;
    padding: @input-padding-vertical-large @input-padding-horizontal;
    height: @input-height-large;
  }

  .input-small() {
    padding: @input-padding-vertical-small @input-padding-horizontal;
    height: @input-height-small;
    border-radius: @btn-border-radius-small;
  }

  .input() {
    display: inline-block;
    width: 100%;
    height: @input-height-base;
    line-height: @line-height-base;
    padding: @input-padding-vertical-base @input-padding-horizontal;
    font-size: @font-size-small;
    border: 1px solid @input-border-color;
    border-radius: @btn-border-radius;
    color: @input-color;
    background-color: @input-bg;
    background-image: none;
    position: relative;
    cursor: text;
    transition: border @transition-time @ease-in-out, background @transition-time @ease-in-out, box-shadow @transition-time @ease-in-out;

    &:hover {
      .hover();
    }

    &:focus {
      .active();
    }

    &[disabled],
    fieldset[disabled] & {
      .disabled();
    }

    // Reset height for textarea
    textarea& {
      max-width: 100%;
      height: auto;
      min-height: @input-height-base;
      vertical-align: bottom;
      font-size: @font-size-base;
    }

    // Size
    &-large {
      .input-large();
    }

    &-small {
      .input-small();
    }
  }

  .input-error() {
    border: 1px solid @error-color;
    &:hover {
      .hover-error;
    }
    &:focus {
      .active-error;
    }
  }

  .input-group(@inputClass) {
    display: table;
    width: 100%;
    border-collapse: separate;
    position: relative;
    font-size: @font-size-small;
    top: 1px; // fixed when using in form inline,

    &-large{
      font-size: @font-size-base;
    }

    // Undo padding and float of grid classes
    &[class*="col-"] {
      float: none;
      padding-left: 0;
      padding-right: 0;
    }

    > [class*="col-"] {
      padding-right: 8px;
    }

    &-prepend,
    &-append,
    > .@{inputClass} {
      display: table-cell;

      //&:not(:first-child):not(:last-child) {
      //    border-radius: 0;
      //}
    }
    &-with-prepend .@{inputClass},
    &-with-prepend&-small .@{inputClass}
    {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
    &-with-append .@{inputClass},
    &-with-append&-small .@{inputClass}
    {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }

    &-prepend .@{css-prefix}btn,
    &-append .@{css-prefix}btn
    {
      border-color: transparent;
      background-color: transparent;
      color: inherit;
      margin: -(@input-padding-vertical-base + 2) (-@input-padding-horizontal);
    }

    &-prepend,
    &-append
    {
      width: 1px; // To make addon/wrap as small as possible
      white-space: nowrap;
      vertical-align: middle;
    }

    .@{inputClass} {
      width: 100%;
      float: left;
      margin-bottom: 0;
      position: relative;
      z-index: 2;
    }

    &-prepend,
    &-append
    {
      padding: @input-padding-vertical-base @input-padding-horizontal;
      font-size: inherit;
      font-weight: normal;
      line-height: 1;
      color: @input-color;
      text-align: center;
      background-color: @input-group-bg;
      border: 1px solid @input-border-color;
      border-radius: @btn-border-radius;

      // Reset Select's style in addon
      .@{css-prefix}select {
        margin: -(@input-padding-vertical-base + 1) (-@input-padding-horizontal);  // lesshint spaceAroundOperator: false

        &-selection {
          background-color: inherit;
          margin: -1px;
          border: 1px solid transparent;
        }

        &-visible .@{css-prefix}select-selection{
          box-shadow: none;
        }
      }
    }

    // Reset rounded corners
    > span > .@{inputClass}:first-child,
    > .@{inputClass}:first-child,
    &-prepend
    {
      border-bottom-right-radius: 0 !important;
      border-top-right-radius: 0 !important;

      // Reset Select's style in addon
      .@{css-prefix}-select .@{css-prefix}-select-selection {
        border-bottom-right-radius: 0;
        border-top-right-radius: 0;
      }
    }

    &-prepend {
      border-right: 0;
    }
    &-append {
      border-left: 0;
    }

    > .@{inputClass}:last-child,
    &-append
    {
      border-bottom-left-radius: 0 !important;
      border-top-left-radius: 0 !important;

      // Reset Select's style in addon
      .@{css-prefix}-select .@{css-prefix}-select-selection {
        border-bottom-left-radius: 0;
        border-top-left-radius: 0;
      }
    }

    // Sizing options
    &-large .@{inputClass},
    &-large > &-prepend,
    &-large > &-append
    {
      .input-large();
    }

    &-small .@{inputClass},
    &-small > &-prepend,
    &-small > &-append
    {
      .input-small();
    }
  }

  .input-group-error{
    &-prepend,
    &-append
    {
      background-color: #fff;
      border: 1px solid @error-color;
      .@{css-prefix}select {
        &-selection {
          background-color: inherit;
          border: 1px solid transparent;
        }
      }
    }
    &-prepend {
      border-right: 0;
    }
    &-append {
      border-left: 0;
    }
  }

</style>



// WEBPACK FOOTER //
// components/Input/Input.vue